import { useState } from 'react';
import {
  Box,
  Button,
  Chip,
  TextField,
  Typography,
  Autocomplete,
  CircularProgress,
} from '@mui/material';
import type { EditData } from '@/types';

interface EditGateStepProps {
  editData: EditData;
  onConfirm: (data: EditData) => Promise<void>;
  onCancel: () => void;
}

export function EditGateStep({ editData, onConfirm, onCancel }: EditGateStepProps) {
  const [regions, setRegions] = useState<string[]>(editData.regions);
  const [keywords, setKeywords] = useState<string[]>(editData.keywords);
  const [menus, setMenus] = useState<string[]>(editData.menus || []);
  const [modifiers, setModifiers] = useState<string[]>(editData.modifiers);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleConfirm = async () => {
    setIsSubmitting(true);
    try {
      await onConfirm({
        regions,
        keywords,
        menus,
        modifiers,
        rpost_threshold: 10,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, py: 2 }}>
      <Typography variant="h6" gutterBottom>
        키워드 생성 데이터 편집
      </Typography>

      <Typography variant="body2" color="text.secondary" gutterBottom>
        Phase 0에서 추출한 데이터를 확인하고 수정할 수 있습니다. 확인 버튼을 클릭하면 Phase 2로 진행됩니다.
      </Typography>

      {/* Regions */}
      <Box>
        <Typography variant="subtitle2" gutterBottom>
          지역 ({regions.length}개)
        </Typography>
        <Autocomplete
          multiple
          freeSolo
          options={[]}
          value={regions}
          onChange={(_, newValue) => setRegions(newValue)}
          renderTags={(value, getTagProps) =>
            value.map((option, index) => (
              <Chip
                label={option}
                {...getTagProps({ index })}
                key={option}
                size="small"
              />
            ))
          }
          renderInput={(params) => (
            <TextField
              {...params}
              placeholder="지역 추가 (Enter로 입력)"
              size="small"
            />
          )}
        />
      </Box>

      {/* Keywords */}
      <Box>
        <Typography variant="subtitle2" gutterBottom>
          키워드 ({keywords.length}개)
        </Typography>
        <Autocomplete
          multiple
          freeSolo
          options={[]}
          value={keywords}
          onChange={(_, newValue) => setKeywords(newValue)}
          renderTags={(value, getTagProps) =>
            value.map((option, index) => (
              <Chip
                label={option}
                {...getTagProps({ index })}
                key={option}
                size="small"
              />
            ))
          }
          renderInput={(params) => (
            <TextField
              {...params}
              placeholder="키워드 추가 (Enter로 입력)"
              size="small"
            />
          )}
        />
      </Box>

      {/* Menus */}
      <Box>
        <Typography variant="subtitle2" gutterBottom>
          메뉴 ({menus.length}개)
        </Typography>
        <Autocomplete
          multiple
          freeSolo
          options={[]}
          value={menus}
          onChange={(_, newValue) => setMenus(newValue)}
          renderTags={(value, getTagProps) =>
            value.map((option, index) => (
              <Chip
                label={option}
                {...getTagProps({ index })}
                key={option}
                size="small"
              />
            ))
          }
          renderInput={(params) => (
            <TextField
              {...params}
              placeholder="메뉴 추가 (Enter로 입력)"
              size="small"
            />
          )}
        />
      </Box>

      {/* Modifiers */}
      <Box>
        <Typography variant="subtitle2" gutterBottom>
          수식어 ({modifiers.length}개)
        </Typography>
        <Autocomplete
          multiple
          freeSolo
          options={[]}
          value={modifiers}
          onChange={(_, newValue) => setModifiers(newValue)}
          renderTags={(value, getTagProps) =>
            value.map((option, index) => (
              <Chip
                label={option}
                {...getTagProps({ index })}
                key={option}
                size="small"
              />
            ))
          }
          renderInput={(params) => (
            <TextField
              {...params}
              placeholder="수식어 추가 (Enter로 입력)"
              size="small"
            />
          )}
        />
      </Box>

       {/* Actions */}
      <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', mt: 2 }}>
        <Button onClick={onCancel} disabled={isSubmitting}>
          취소
        </Button>
        <Button
          variant="contained"
          onClick={handleConfirm}
          disabled={isSubmitting}
          startIcon={isSubmitting ? <CircularProgress size={16} /> : null}
        >
          {isSubmitting ? '진행 중...' : '확인 및 계속'}
        </Button>
      </Box>
    </Box>
  );
}
